// Phase 3.3 (beads hh-w3x) — CalendarScreen.
//
// Ported from designproject/files/screens-calendar.jsx (`CalendarScreen`). The
// design read a static `window.TASKS`; here tasks come from the live
// GET /api/v1/tasks?from=&to= endpoint (Phase 3.2, TasksController). The static
// MonthStrip mini-calendar was dropped — it carried hard-coded May-2026 data
// with no backend, and the three view-mode chips (week / month / quarter) now
// drive a real date-range query instead.
//
// Per-task actions: "Сделано" → PATCH isDone; "Перенести" → opens a date
// picker (SnoozeSheet) prefilled with base date + SNOOZE_DAYS, then PATCH
// snoozeUntil with the chosen date (aidepo-57.6); seasonal tasks reject snooze
// server-side, so the snooze button is hidden for them. Done maintenance rows
// show their completion date (aidepo-57.4). Tapping a task with an equipmentId
// routes to eq_card.

import type { JSX } from 'preact';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { T, Icon, Chip, Btn, SectionLabel, type IconName } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi, ScreenParams } from '../../router';
import { tasks as tasksApi, humanizeError } from '../../api';
import type { TaskRow } from '../../api/endpoints/tasks.js';

export interface CalendarScreenProps {
  readonly router: RouterApi;
  /**
   * Параметры вкладки из роутера (aidepo-63.2). Поддерживается `mode` —
   * начальный список-горизонт (`week` | `month` | `quarter`); при прямом
   * открытии вкладки параметр отсутствует → фолбэк на 'month'.
   */
  readonly params?: ScreenParams;
}

type ViewMode = 'week' | 'month' | 'quarter';

/** aidepo-63.5: основной переключатель экрана — список-горизонт vs сетка-месяц. */
type CalView = 'list' | 'grid';

const VIEWS: readonly (readonly [CalView, string, IconName])[] = [
  ['list', 'Список', 'list'],
  ['grid', 'Сетка', 'cal'],
] as const;

const MODES: readonly (readonly [ViewMode, string])[] = [
  ['week', 'Неделя'],
  ['month', 'Месяц'],
  ['quarter', 'Квартал'],
] as const;

/** Типобезопасное чтение начального режима списка из параметров вкладки. */
function initialMode(params: ScreenParams | undefined): ViewMode {
  const m = params?.['mode'];
  return m === 'week' || m === 'month' || m === 'quarter' ? m : 'month';
}

type LoadStatus = 'loading' | 'ready' | 'error';

const MONTHS_RU = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
] as const;
const WEEKDAYS_RU = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'] as const;
const SNOOZE_DAYS = 7;

// aidepo-63.4: тогл «Показать выполненные» — sticky на сессию (sessionStorage).
const SHOW_DONE_KEY = 'calendar.showDone';

function readShowDone(): boolean {
  try {
    return sessionStorage.getItem(SHOW_DONE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeShowDone(value: boolean): void {
  try {
    if (value) sessionStorage.setItem(SHOW_DONE_KEY, '1');
    else sessionStorage.removeItem(SHOW_DONE_KEY);
  } catch {
    /* private mode — не критично */
  }
}

function describeError(err: unknown): string {
  return humanizeError(err, 'load');
}

/** [from, to] ISO range for a view mode, anchored on `now`. */
function rangeFor(mode: ViewMode, now: Date): { from: string; to: string } {
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  if (mode === 'week') to.setDate(to.getDate() + 7);
  else if (mode === 'month') to.setMonth(to.getMonth() + 1);
  else to.setMonth(to.getMonth() + 3);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** Human range caption, e.g. "Май–Июнь" or single month. */
function rangeCaption(mode: ViewMode, now: Date): string {
  const end = new Date(now);
  if (mode === 'week') end.setDate(end.getDate() + 7);
  else if (mode === 'month') end.setMonth(end.getMonth() + 1);
  else end.setMonth(end.getMonth() + 3);
  const cap = (m: number): string => {
    const name = MONTHS_RU[m] ?? '';
    return name.charAt(0).toUpperCase() + name.slice(1, -1);
  };
  const a = cap(now.getMonth());
  const b = cap(end.getMonth());
  return a === b ? a : `${a}–${b}`;
}

function parseDate(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Дата в формате «1 января 2026 г.» — как в карточке оборудования. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Локальная YYYY-MM-DD строка для value у <input type="date"> и ключа дня. */
function toDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ─────────────────────────────────────────────────────────
// Сетка-месяц (aidepo-63.5)
// ─────────────────────────────────────────────────────────

const MONTHS_RU_NOM = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
] as const;
// Понедельник-первый ряд заголовков сетки.
const GRID_WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'] as const;

/** Первое число месяца (локальная полночь) для даты. */
function firstOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}

/** Подпись «Май 2026» для месяца. */
function monthCaption(d: Date): string {
  return `${MONTHS_RU_NOM[d.getMonth()] ?? ''} ${d.getFullYear()}`;
}

/** [from, to] ISO-диапазон видимого месяца: 1-е 00:00 … 1-е след. месяца. */
function monthRange(monthStart: Date): { from: string; to: string } {
  const from = firstOfMonth(monthStart);
  const to = new Date(from.getFullYear(), from.getMonth() + 1, 1, 0, 0, 0, 0);
  return { from: from.toISOString(), to: to.toISOString() };
}

/**
 * Раскладка месяца в сетку 6×7, с понедельника. Возвращает плоский список
 * ячеек; `inMonth=false` — дни-добивки соседних месяцев (visual padding).
 */
function buildMonthCells(monthStart: Date): readonly { date: Date; inMonth: boolean }[] {
  const first = firstOfMonth(monthStart);
  // getDay(): 0=вс…6=сб → сдвиг к понедельнику-первому (пн=0…вс=6).
  const lead = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - lead);
  const cells: { date: Date; inMonth: boolean }[] = [];
  for (let i = 0; i < 42; i += 1) {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i, 0, 0, 0, 0);
    cells.push({ date, inMonth: date.getMonth() === first.getMonth() });
  }
  return cells;
}

/** Группировка задач по локальному дню (YYYY-MM-DD) для маркеров в ячейках. */
function groupByDay(rows: readonly TaskRow[]): ReadonlyMap<string, readonly TaskRow[]> {
  const map = new Map<string, TaskRow[]>();
  for (const t of rows) {
    const d = parseDate(t.date);
    if (d === null) continue;
    const key = toDateInputValue(d);
    const bucket = map.get(key);
    if (bucket) bucket.push(t);
    else map.set(key, [t]);
  }
  return map;
}

// ─────────────────────────────────────────────────────────
// Task row
// ─────────────────────────────────────────────────────────

function safetyIcon(task: TaskRow): IconName {
  return task.isSeasonal ? 'flame' : 'shield';
}

function CalRow({
  task,
  busy,
  onOpen,
  onDone,
  onSnooze,
}: {
  readonly task: TaskRow;
  readonly busy: boolean;
  readonly onOpen: () => void;
  readonly onDone: () => void;
  readonly onSnooze: () => void;
}): JSX.Element {
  const date = parseDate(task.date);
  const tone = task.isSafety
    ? { bg: T.safetySoft, fg: T.safety, bd: '#E8D7A8' }
    : { bg: T.surface, fg: T.ink, bd: T.border };
  const canOpen = task.equipmentId !== null;
  const canSnooze = !task.isSeasonal;

  return (
    <div
      style={{
        padding: 12,
        marginTop: 8,
        background: tone.bg,
        border: `1px solid ${tone.bd}`,
        borderRadius: 12,
        opacity: task.isDone ? 0.55 : 1,
      }}
    >
      <div
        onClick={canOpen ? onOpen : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          cursor: canOpen ? 'pointer' : 'default',
        }}
      >
        <div
          style={{
            width: 48,
            padding: '6px 0',
            textAlign: 'center',
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: T.ink3,
              textTransform: 'uppercase',
            }}
          >
            {date ? WEEKDAYS_RU[date.getDay()] : '—'}
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: T.ink,
              lineHeight: 1.1,
            }}
          >
            {date ? date.getDate() : '—'}
          </div>
        </div>
        {task.isSafety && (
          <Icon name={safetyIcon(task)} size={18} color={tone.fg} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 650,
              color: T.ink,
              display: 'flex',
              gap: 6,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                textDecoration: task.isDone ? 'line-through' : 'none',
              }}
            >
              {task.title}
            </span>
            {task.isSafety && <Chip tone="safety">safety</Chip>}
            {task.isSeasonal && <Chip tone="neutral">сезон</Chip>}
          </div>
          {/* aidepo-57.4: дата выполнения — только для выполненных задач. */}
          {task.isDone && task.lastCompletedAt != null && (
            <div style={{ fontSize: 12, color: T.ink3, marginTop: 4 }}>
              Выполнено {formatDate(task.lastCompletedAt)}
            </div>
          )}
        </div>
        {canOpen && <Icon name="chevR" size={16} color={T.ink4} />}
      </div>

      {!task.isDone && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <Btn
            kind="primary"
            size="sm"
            icon="check"
            onClick={onDone}
            disabled={busy}
            style={{ flex: 1 }}
          >
            Сделано
          </Btn>
          {canSnooze && (
            <Btn
              kind="outline"
              size="sm"
              icon="cal"
              onClick={onSnooze}
              disabled={busy}
              style={{ flex: 1 }}
            >
              Перенести
            </Btn>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// MonthGrid — вид «Сетка» (aidepo-63.5)
// ─────────────────────────────────────────────────────────

/** Маркеры для дня: точка-наличие + safety-треугольник + галочка выполненных. */
function DayMarkers({ tasks }: { readonly tasks: readonly TaskRow[] }): JSX.Element | null {
  if (tasks.length === 0) return null;
  const hasPending = tasks.some((t) => !t.isDone);
  const hasDone = tasks.some((t) => t.isDone);
  const hasSafety = tasks.some((t) => t.isSafety && !t.isDone);
  return (
    <div
      style={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 2,
        lineHeight: 1,
        height: 10,
      }}
    >
      {hasSafety && (
        <span style={{ fontSize: 9, color: T.safety }} aria-hidden="true">
          ▲
        </span>
      )}
      {hasPending && (
        <span style={{ fontSize: 11, color: T.brand }} aria-hidden="true">
          ●
        </span>
      )}
      {hasDone && (
        <span style={{ fontSize: 10, color: T.ink4 }} aria-hidden="true">
          {hasPending ? '•' : '✓'}
        </span>
      )}
    </div>
  );
}

function MonthGrid({
  monthStart,
  byDay,
  onPrev,
  onNext,
  onPickDay,
}: {
  readonly monthStart: Date;
  readonly byDay: ReadonlyMap<string, readonly TaskRow[]>;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  readonly onPickDay: (date: Date) => void;
}): JSX.Element {
  const cells = useMemo(() => buildMonthCells(monthStart), [monthStart]);
  const todayKey = toDateInputValue(new Date());

  return (
    <div>
      {/* ‹ Май 2026 › */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 0 12px',
        }}
      >
        <button
          type="button"
          onClick={onPrev}
          aria-label="Предыдущий месяц"
          style={navArrowStyle}
        >
          <Icon name="chevL" size={18} color={T.ink2} />
        </button>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.ink }}>
          {monthCaption(monthStart)}
        </div>
        <button
          type="button"
          onClick={onNext}
          aria-label="Следующий месяц"
          style={navArrowStyle}
        >
          <Icon name="chevR" size={18} color={T.ink2} />
        </button>
      </div>

      {/* Заголовки дней недели */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 4,
          marginBottom: 4,
        }}
      >
        {GRID_WEEKDAYS.map((w) => (
          <div
            key={w}
            style={{
              textAlign: 'center',
              fontSize: 10.5,
              fontWeight: 700,
              color: T.ink3,
              textTransform: 'uppercase',
            }}
          >
            {w}
          </div>
        ))}
      </div>

      {/* Ячейки дней */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 4,
        }}
      >
        {cells.map(({ date, inMonth }) => {
          const key = toDateInputValue(date);
          const tasks = byDay.get(key) ?? [];
          const isToday = key === todayKey;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onPickDay(date)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                aspectRatio: '1 / 1',
                padding: '6px 0 0',
                background: isToday ? T.brandSoft : T.surface,
                border: `1px solid ${isToday ? T.brandSoft2 : T.border}`,
                borderRadius: 10,
                cursor: 'pointer',
                fontFamily: T.font,
                opacity: inMonth ? 1 : 0.4,
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  fontWeight: isToday ? 800 : 600,
                  color: inMonth ? T.ink : T.ink3,
                  lineHeight: 1,
                }}
              >
                {date.getDate()}
              </span>
              <DayMarkers tasks={tasks} />
            </button>
          );
        })}
      </div>

      {/* Легенда маркеров */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          padding: '14px 4px 0',
          fontSize: 11.5,
          color: T.ink3,
        }}
      >
        <span>
          <span style={{ color: T.brand }}>●</span> запланировано
        </span>
        <span>
          <span style={{ color: T.safety }}>▲</span> safety
        </span>
        <span>
          <span style={{ color: T.ink4 }}>✓</span> выполнено
        </span>
      </div>
    </div>
  );
}

const navArrowStyle: JSX.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 18,
  background: T.surface,
  border: `1px solid ${T.border}`,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
};

// ─────────────────────────────────────────────────────────
// DayView — под-состояние экрана «День» (aidepo-63.5)
// ─────────────────────────────────────────────────────────

function DayView({
  date,
  tasks,
  busyId,
  actionError,
  onBack,
  onOpenEquipment,
  onDone,
  onSnooze,
  onAdd,
}: {
  readonly date: Date;
  readonly tasks: readonly TaskRow[];
  readonly busyId: string | null;
  readonly actionError: string | null;
  readonly onBack: () => void;
  readonly onOpenEquipment: (task: TaskRow) => void;
  readonly onDone: (task: TaskRow) => void;
  readonly onSnooze: (task: TaskRow) => void;
  readonly onAdd: () => void;
}): JSX.Element {
  const title = `${date.getDate()} ${MONTHS_RU[date.getMonth()] ?? ''}`;
  const subtitle = date.toLocaleDateString('ru-RU', { weekday: 'long', year: 'numeric' });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: T.bg,
      }}
    >
      <ScreenHeader title={title} subtitle={subtitle} onBack={onBack} />

      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 24px' }}>
        {actionError !== null && (
          <div
            style={{
              fontSize: 12.5,
              color: T.danger,
              fontWeight: 600,
              padding: '6px 4px',
            }}
          >
            {actionError}
          </div>
        )}

        {tasks.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '48px 20px',
              color: T.ink3,
            }}
          >
            <Icon name="leaf" size={32} color={T.ink4} />
            <div
              style={{
                fontSize: 14.5,
                fontWeight: 700,
                color: T.ink2,
                marginTop: 8,
              }}
            >
              В этот день задач нет
            </div>
            <div style={{ fontSize: 12.5, marginTop: 4 }}>
              ТО появляются из регламентов оборудования и сезонных чек-листов.
            </div>
            <Btn kind="outline" size="sm" icon="plus" onClick={onAdd} style={{ marginTop: 14 }}>
              Добавить оборудование
            </Btn>
          </div>
        ) : (
          tasks.map((task) => (
            <CalRow
              key={task.id}
              task={task}
              busy={busyId === task.id}
              onOpen={() => onOpenEquipment(task)}
              onDone={() => onDone(task)}
              onSnooze={() => onSnooze(task)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// CalendarScreen
// ─────────────────────────────────────────────────────────

export function CalendarScreen({ router, params }: CalendarScreenProps): JSX.Element {
  const [mode, setMode] = useState<ViewMode>(() => initialMode(params));
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [rows, setRows] = useState<readonly TaskRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState<number>(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // aidepo-57.6: задача, для которой открыт пикер даты переноса (или null).
  const [snoozeTask, setSnoozeTask] = useState<TaskRow | null>(null);
  // aidepo-63.4: по умолчанию выполненные ТО скрыты; тогл sticky на сессию.
  const [showDone, setShowDone] = useState<boolean>(() => readShowDone());
  // aidepo-63.5: переключатель Список / Сетка; видимый месяц сетки; выбранный
  // день (под-состояние «День» — НЕ запись в маршрутах роутера).
  const [view, setView] = useState<CalView>('list');
  const [gridMonth, setGridMonth] = useState<Date>(() => firstOfMonth(new Date()));
  const [dayDate, setDayDate] = useState<Date | null>(null);

  const now = useMemo(() => new Date(), []);
  // aidepo-63.5: список тянет горизонт по mode, сетка/день — весь видимый месяц.
  const range = useMemo(
    () => (view === 'grid' ? monthRange(gridMonth) : rangeFor(mode, now)),
    [view, gridMonth, mode, now],
  );
  const caption = useMemo(() => rangeCaption(mode, now), [mode, now]);

  // aidepo-63.4: клиентский фильтр по уже загруженным строкам.
  const visibleRows = useMemo(
    () => (showDone ? rows : rows.filter((t) => !t.isDone)),
    [rows, showDone],
  );
  const doneCount = useMemo(() => rows.filter((t) => t.isDone).length, [rows]);

  const toggleShowDone = useCallback((): void => {
    setShowDone((prev) => {
      const next = !prev;
      writeShowDone(next);
      return next;
    });
  }, []);

  const reload = useCallback((): void => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError(null);
    tasksApi
      .listTasks(range.from, range.to)
      .then((res) => {
        if (cancelled) return;
        setRows(res);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(describeError(err));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [range.from, range.to, nonce]);

  const runPatch = useCallback(
    (task: TaskRow, body: { isDone?: boolean; snoozeUntil?: string }): void => {
      setBusyId(task.id);
      setActionError(null);
      tasksApi
        .patchTask(task.id, body)
        .then(() => {
          setBusyId(null);
          reload();
        })
        .catch((err: unknown) => {
          setBusyId(null);
          setActionError(describeError(err));
        });
    },
    [reload],
  );

  const markDone = useCallback(
    (task: TaskRow): void => runPatch(task, { isDone: true }),
    [runPatch],
  );

  // aidepo-57.6: «Перенести» открывает пикер даты вместо жёсткого +7 дней.
  const openSnooze = useCallback(
    (task: TaskRow): void => setSnoozeTask(task),
    [],
  );

  const confirmSnooze = useCallback(
    (task: TaskRow, when: Date): void => {
      setSnoozeTask(null);
      runPatch(task, { snoozeUntil: when.toISOString() });
    },
    [runPatch],
  );

  const openEquipment = useCallback(
    (task: TaskRow): void => {
      if (task.equipmentId !== null) {
        router.go('eq_card', { id: task.equipmentId });
      }
    },
    [router],
  );

  // aidepo-63.5: переключение Список/Сетка. При уходе из сетки закрываем «День»;
  // при входе в сетку синхронизируем видимый месяц с текущей датой.
  const switchView = useCallback((next: CalView): void => {
    setView(next);
    setDayDate(null);
    if (next === 'grid') setGridMonth(firstOfMonth(new Date()));
  }, []);

  const goPrevMonth = useCallback((): void => {
    setGridMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1, 0, 0, 0, 0));
  }, []);
  const goNextMonth = useCallback((): void => {
    setGridMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1, 0, 0, 0, 0));
  }, []);

  // aidepo-63.5: маркеры сетки строятся по всем строкам месяца (выполненные
  // показываются отдельным маркером — день не скрывается из-за выполненности).
  const byDay = useMemo(() => groupByDay(rows), [rows]);

  // Задачи выбранного дня для под-состояния «День» (выполненные видны).
  const dayTasks = useMemo<readonly TaskRow[]>(() => {
    if (dayDate === null) return [];
    const key = toDateInputValue(dayDate);
    return byDay.get(key) ?? [];
  }, [dayDate, byDay]);

  // aidepo-63.5: под-состояние «День» — отдельный рендер поверх вкладки.
  if (dayDate !== null) {
    return (
      <DayView
        date={dayDate}
        tasks={dayTasks}
        busyId={busyId}
        actionError={actionError}
        onBack={() => setDayDate(null)}
        onOpenEquipment={openEquipment}
        onDone={markDone}
        onSnooze={openSnooze}
        onAdd={() => router.go('catalog_eq')}
      />
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: T.bg,
      }}
    >
      <ScreenHeader title="Календарь" subtitle="ТО, сезонные задачи, замены" />

      {/* aidepo-63.5: переключатель Список / Сетка */}
      <div style={{ padding: '0 16px 12px' }}>
        <div
          style={{
            display: 'flex',
            gap: 6,
            background: T.surface,
            padding: 4,
            borderRadius: 12,
            border: `1px solid ${T.border}`,
          }}
        >
          {VIEWS.map(([key, label, icon]) => (
            <button
              key={key}
              type="button"
              onClick={() => switchView(key)}
              aria-pressed={view === key}
              style={{
                flex: 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '8px 0',
                fontSize: 13,
                fontWeight: 650,
                background: view === key ? T.ink : 'transparent',
                color: view === key ? '#fff' : T.ink2,
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontFamily: T.font,
              }}
            >
              <Icon name={icon} size={15} color={view === key ? '#fff' : T.ink2} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* List-mode chips Неделя/Месяц/Квартал — только в виде «Список». */}
      {view === 'list' && (
        <div style={{ padding: '0 16px 12px' }}>
          <div
            style={{
              display: 'flex',
              gap: 6,
              background: T.surface,
              padding: 4,
              borderRadius: 12,
              border: `1px solid ${T.border}`,
            }}
          >
            {MODES.map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setMode(key)}
                style={{
                  flex: 1,
                  padding: '8px 0',
                  fontSize: 13,
                  fontWeight: 650,
                  background: mode === key ? T.ink : 'transparent',
                  color: mode === key ? '#fff' : T.ink2,
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontFamily: T.font,
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* aidepo-63.4: тогл «Показать выполненные» (sticky на сессию). */}
      {view === 'list' && status === 'ready' && (rows.length > 0 || showDone) && (
        <div
          style={{
            padding: '0 16px 12px',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={toggleShowDone}
            aria-pressed={showDone}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              fontSize: 12.5,
              fontWeight: 650,
              color: showDone ? '#fff' : T.ink2,
              background: showDone ? T.ink : T.surface,
              border: `1px solid ${showDone ? T.ink : T.border}`,
              borderRadius: 999,
              cursor: 'pointer',
              fontFamily: T.font,
            }}
          >
            <Icon name="check" size={13} color={showDone ? '#fff' : T.ink3} />
            {showDone ? 'Скрыть выполненные' : 'Показать выполненные'}
            {doneCount > 0 && ` (${doneCount})`}
          </button>
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 24px' }}>
        {view === 'list' && (
          <SectionLabel style={{ padding: '6px 0 8px' }}>{caption}</SectionLabel>
        )}

        {status === 'loading' && (
          <div style={{ fontSize: 13, color: T.ink3, padding: '20px 4px' }}>
            Загружаю задачи…
          </div>
        )}

        {status === 'error' && (
          <div
            style={{
              background: T.dangerSoft,
              border: '1px solid #E9C7BF',
              borderRadius: 12,
              padding: 14,
            }}
          >
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.danger }}>
              Не удалось загрузить
            </div>
            {error !== null && (
              <div style={{ fontSize: 12, color: T.ink2, marginTop: 2 }}>
                {error}
              </div>
            )}
            <Btn
              kind="outline"
              size="sm"
              onClick={reload}
              style={{ marginTop: 10 }}
            >
              Повторить
            </Btn>
          </div>
        )}

        {/* aidepo-63.5: вид «Сетка» — месяц с навигацией по месяцам. */}
        {view === 'grid' && status === 'ready' && (
          <MonthGrid
            monthStart={gridMonth}
            byDay={byDay}
            onPrev={goPrevMonth}
            onNext={goNextMonth}
            onPickDay={(date) => setDayDate(date)}
          />
        )}

        {view === 'list' && status === 'ready' && visibleRows.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              padding: '48px 20px',
              color: T.ink3,
            }}
          >
            <Icon name="leaf" size={32} color={T.ink4} />
            <div
              style={{
                fontSize: 14.5,
                fontWeight: 700,
                color: T.ink2,
                marginTop: 8,
              }}
            >
              {mode === 'week'
                ? 'Спокойная неделя'
                : mode === 'month'
                  ? 'Спокойный месяц'
                  : 'Спокойный квартал'}
            </div>
            <div style={{ fontSize: 12.5, marginTop: 4 }}>
              {/* aidepo-63.4: все задачи диапазона выполнены и скрыты. */}
              {rows.length > 0
                ? 'Все задачи ТО в этом диапазоне выполнены.'
                : 'Нет задач ТО в этом диапазоне.'}
            </div>
          </div>
        )}

        {view === 'list' && status === 'ready' && visibleRows.length > 0 && (
          <>
            {actionError !== null && (
              <div
                style={{
                  fontSize: 12.5,
                  color: T.danger,
                  fontWeight: 600,
                  padding: '6px 4px',
                }}
              >
                {actionError}
              </div>
            )}
            {visibleRows.map((task) => (
              <CalRow
                key={task.id}
                task={task}
                busy={busyId === task.id}
                onOpen={() => openEquipment(task)}
                onDone={() => markDone(task)}
                onSnooze={() => openSnooze(task)}
              />
            ))}
          </>
        )}
      </div>

      {snoozeTask !== null && (
        <SnoozeSheet
          task={snoozeTask}
          onClose={() => setSnoozeTask(null)}
          onConfirm={confirmSnooze}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Snooze (reschedule) sheet — aidepo-57.6
// ─────────────────────────────────────────────────────────

/**
 * Нижний лист выбора новой даты для «Перенести». Предзаполняется значением
 * по умолчанию — базовая дата задачи + SNOOZE_DAYS. Пользователь меняет дату
 * нативным <input type="date"> и подтверждает; отмена ничего не делает.
 */
function SnoozeSheet({
  task,
  onClose,
  onConfirm,
}: {
  readonly task: TaskRow;
  readonly onClose: () => void;
  readonly onConfirm: (task: TaskRow, when: Date) => void;
}): JSX.Element {
  const defaultDate = useMemo(() => {
    const base = parseDate(task.date) ?? new Date();
    const next = new Date(base);
    next.setHours(0, 0, 0, 0);
    next.setDate(next.getDate() + SNOOZE_DAYS);
    return next;
  }, [task.date]);

  const [value, setValue] = useState<string>(() => toDateInputValue(defaultDate));

  const confirm = (): void => {
    const parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return;
    const [y, m, d] = parts as [number, number, number];
    // Локальная полночь выбранного дня — без сдвига часового пояса.
    const when = new Date(y, m - 1, d, 0, 0, 0, 0);
    if (Number.isNaN(when.getTime())) return;
    onConfirm(task, when);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Перенести задачу"
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(31,27,22,0.4)',
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.bg,
          borderRadius: '22px 22px 0 0',
          padding: '16px 16px 24px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            width: 36,
            height: 4,
            background: T.borderStrong,
            borderRadius: 2,
            margin: '0 auto 14px',
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 4,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700, color: T.ink }}>
            Перенести задачу
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: T.ink3,
              padding: 0,
            }}
          >
            <Icon name="close" size={20} />
          </button>
        </div>
        <div style={{ fontSize: 13, color: T.ink3, marginBottom: 12 }}>
          {task.title}
        </div>

        <label
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: T.ink2,
            marginBottom: 6,
          }}
        >
          Новая дата
        </label>
        <input
          type="date"
          value={value}
          onInput={(e) => setValue((e.currentTarget as HTMLInputElement).value)}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '12px 14px',
            fontSize: 15,
            fontFamily: T.font,
            color: T.ink,
            background: T.surface,
            border: `1px solid ${T.borderStrong}`,
            borderRadius: 12,
          }}
        />

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <Btn kind="ghost" onClick={onClose} style={{ flex: 1 }}>
            Отмена
          </Btn>
          <Btn
            kind="primary"
            icon="cal"
            onClick={confirm}
            disabled={value === ''}
            style={{ flex: 1.5 }}
          >
            Перенести
          </Btn>
        </div>
      </div>
    </div>
  );
}
